import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import classNames from "classnames";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";

import { toastState } from "../../features/recoil";

export type ToastProps = {
    id: string;
    type?: "success" | "info" | "error" | "warning";
    content?: any;
    uniqueId?: string | null;
};

const TOAST_TYPES = {
    success: { border: "border-success", code: "#00ad59", text: "text-success" },
    error: { border: "border-error", code: "#f4645e", text: "text-error" },
    info: { border: "border-info", code: "#17a2b8", text: "text-info" },
    warning: { border: "border-warning", code: "#ffb100", text: "text-warning" },
};

const Toast = (props: ToastProps) => {
    const { id, content, type } = props;
    const { t } = useTranslation();
    const setToast = useSetRecoilState(toastState);

    const handleClose = () => {
        setToast((prev) => prev.filter((v) => id !== v.id));
    };

    useEffect(() => {
        const timeout = window.setTimeout(
            () => {
                handleClose();
            },
            type === "error" ? 3000 : 1600,
        );

        return () => {
            window.clearTimeout(timeout);
        };
    }, []);

    return (
        <motion.div initial={{ translateX: 32, opacity: 0.5 }} animate={{ translateX: 0, opacity: 1 }}>
            <div
                className={classNames(
                    "relative w-80 shadow border rounded bg-background",
                    (type && TOAST_TYPES[type].border) || "#cccccc",
                )}
            >
                <motion.div
                    initial={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        width: "100%",
                        height: "1px",
                        backgroundColor: (type && TOAST_TYPES[type].code) || "#cccccc",
                    }}
                    animate={{ width: "0%" }}
                    transition={{ duration: type === "error" ? 2.8 : 1.4 }}
                ></motion.div>
                <div className={classNames("p-4 font-semibold", type && TOAST_TYPES[type].text)}>
                    {typeof content === "string" ? t(content) : content}
                </div>
            </div>
        </motion.div>
    );
};

const CommonToast = () => {
    const toast = useRecoilValue(toastState);

    return createPortal(
        <div className="fixed right-4 bottom-4 space-y-2 z-[9999]">
            {toast.map((props) => {
                return <Toast key={props.id} {...props} />;
            })}
        </div>,
        document.body,
    );
};

export default CommonToast;
